import { BaseApiService, type ApiPaths } from './base';
import { tokenStorage, type TokenData } from '../utils/tokenStorage';
import type {
  OAuthProviderValue,
  OAuthServiceValue,
  UnifiedOAuthProviderValue,
} from '../constants';

// Extract types from generated API paths

type ActivateRequest =
  ApiPaths['/auth/activate']['post']['requestBody']['content']['application/json'];
type ActivateResponse =
  ApiPaths['/auth/activate']['post']['responses'][200]['content']['application/json'];
type ProfileResponse =
  ApiPaths['/users/me']['get']['responses'][200]['content']['application/json'];
type PasswordlessVerifyRequest =
  ApiPaths['/auth/passwordless/verify']['post']['requestBody']['content']['application/json'];
type PasswordlessVerifyResponse =
  ApiPaths['/auth/passwordless/verify']['post']['responses'][200]['content']['application/json'];
type LogoutResponse =
  ApiPaths['/auth/logout']['post']['responses'][200]['content']['application/json'];
type DeleteAccountRequest =
  ApiPaths['/auth/account']['delete']['requestBody']['content']['application/json'];
type DeleteAccountResponse =
  ApiPaths['/auth/account']['delete']['responses'][200]['content']['application/json'];
type RefreshTokenRequest =
  ApiPaths['/auth/refresh']['post']['requestBody']['content']['application/json'];
type RefreshTokenResponse =
  ApiPaths['/auth/refresh']['post']['responses'][200]['content']['application/json'];
type VerifyDeletionStatusRequest =
  ApiPaths['/auth/facebook/verify-deletion-status']['post']['requestBody']['content']['application/json'];
type VerifyDeletionStatusResponse =
  ApiPaths['/auth/facebook/verify-deletion-status']['post']['responses'][200]['content']['application/json'];

// OAuth provider types
type OAuthProviderRedirectResponse =
  ApiPaths['/auth/oauth/{provider}']['get']['responses'][302]['content']['application/json'];
type OAuthCallbackResponse =
  ApiPaths['/auth/oauth/{provider}/callback']['get']['responses'][200]['content']['application/json'];

export type AuthOAuthCallbackSuccessResponse = OAuthCallbackResponse['data'];

export interface AuthOAuthPendingRegistrationProfile {
  email: string;
  name: string | undefined;
  avatar_url: string | undefined;
  provider: string;
}

export interface AuthOAuthPendingRegistrationResponse {
  pending_registration_token: string;
  expires_in: number;
  is_new_user: true;
  action: 'register';
  requires_terms_acceptance: true;
  profile: AuthOAuthPendingRegistrationProfile;
}

export interface AuthOAuthFinalizeRequest {
  pending_registration_token: string;
  accepted_terms: boolean;
  marketingEmails: boolean | undefined;
}

export type AuthOAuthFinalizeResponse = AuthOAuthCallbackSuccessResponse;

export type AuthActivateRequest = ActivateRequest;
export type AuthActivateResponse = ActivateResponse['data'];
export type AuthUser = ActivateResponse['data']['user'];
export type AuthProfileResponse = ProfileResponse['data'];
export type AuthPasswordlessVerifyRequest = PasswordlessVerifyRequest;
export type AuthPasswordlessVerifyResponse = PasswordlessVerifyResponse['data'];
export type AuthLogoutResponse = LogoutResponse['data'];
export type AuthDeleteAccountRequest = DeleteAccountRequest;
export type AuthDeleteAccountResponse = DeleteAccountResponse['data'];
export type AuthRefreshTokenRequest = RefreshTokenRequest;
export type AuthRefreshTokenResponse = RefreshTokenResponse['data'];
export type AuthVerifyDeletionStatusRequest = VerifyDeletionStatusRequest;
export type AuthVerifyDeletionStatusResponse =
  VerifyDeletionStatusResponse['data'];
export type AuthOAuthProviderRedirectResponse =
  OAuthProviderRedirectResponse['data'];
export type AuthOAuthCallbackResponse =
  | AuthOAuthCallbackSuccessResponse
  | AuthOAuthPendingRegistrationResponse;

// Unified email auth types - will be available after type generation
// @ts-ignore - Types will be generated from OpenAPI schema
type UnifiedEmailAuthRequest =
  ApiPaths['/auth/email']['post']['requestBody']['content']['application/json'];
// @ts-ignore - Types will be generated from OpenAPI schema
type UnifiedEmailAuthResponse =
  ApiPaths['/auth/email']['post']['responses'][200]['content']['application/json'];

export interface AuthUnifiedEmailConsentPayload {
  accepted_terms: boolean | undefined;
  marketingEmails: boolean | undefined;
}

type AuthUnifiedEmailAuthRequestWithConsent = UnifiedEmailAuthRequest &
  AuthUnifiedEmailConsentPayload;

export type AuthUnifiedEmailAuthRequest = UnifiedEmailAuthRequest;
export type AuthUnifiedEmailAuthResponse = UnifiedEmailAuthResponse['data'] & {
  flow: 'login' | 'register';
};
// Use passwordless verify types for unified email verify
export type AuthUnifiedEmailVerifyRequest = PasswordlessVerifyRequest;
export type AuthUnifiedEmailVerifyResponse = PasswordlessVerifyResponse['data'];

export function isAuthOAuthPendingRegistrationResponse(
  response: AuthOAuthCallbackResponse,
): response is AuthOAuthPendingRegistrationResponse {
  return (
    'requires_terms_acceptance' in response &&
    response.requires_terms_acceptance === true
  );
}

export function isAuthOAuthCallbackSuccessResponse(
  response: AuthOAuthCallbackResponse | AuthOAuthFinalizeResponse,
): response is AuthOAuthCallbackSuccessResponse {
  return 'access_token' in response && 'refresh_token' in response;
}

class AuthApiService extends BaseApiService {
  /**
   * Activate user account with token
   * Uses generated types for better type safety
   */
  async activate(
    token: string,
    locale: string,
    signal?: AbortSignal,
  ): Promise<ActivateResponse['data']> {
    const response = await this.makeRequest<ActivateResponse['data']>(
      '/auth/activate',
      {
        method: 'POST',
        body: { token, locale },
        requiresAuth: false,
        signal,
      },
    );

    // Store tokens after successful activation if they exist in the response
    if (response.access_token && response.refresh_token) {
      const tokenData: TokenData = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
      tokenStorage.setTokens(tokenData);
    }

    return response;
  }

  /**
   * Verify passwordless login
   * Verifies the token from email and authenticates user
   */
  async verifyPasswordlessLogin(
    data: PasswordlessVerifyRequest,
    signal?: AbortSignal,
  ): Promise<PasswordlessVerifyResponse['data']> {
    const response = await this.makeRequest<PasswordlessVerifyResponse['data']>(
      '/auth/passwordless/verify',
      {
        method: 'POST',
        body: data,
        requiresAuth: false,
        signal,
      },
    );

    // Store tokens after successful verification
    if (response.access_token && response.refresh_token) {
      const tokenData: TokenData = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
      tokenStorage.setTokens(tokenData);
    }

    return response;
  }

  /**
   * Logout user
   * Logs out the current authenticated user
   */
  async logout(): Promise<LogoutResponse['data']> {
    try {
      const response = await this.makeRequest<LogoutResponse['data']>(
        '/auth/logout',
        {
          method: 'POST',
          body: {}, // Add empty body to satisfy content-type requirement
        },
      );

      // Clear stored tokens after successful logout
      tokenStorage.clearAll();

      return response;
    } catch (error) {
      // Even if logout fails on server, clear local tokens
      tokenStorage.clearAll();
      throw error;
    }
  }

  /**
   * Delete user account
   * Permanently deletes the current authenticated user account
   */
  async deleteAccount(
    data: DeleteAccountRequest,
  ): Promise<DeleteAccountResponse['data']> {
    try {
      const response = await this.makeRequest<DeleteAccountResponse['data']>(
        '/auth/account',
        {
          method: 'DELETE',
          body: data,
        },
      );

      // Clear stored tokens after successful account deletion
      tokenStorage.clearAll();

      return response;
    } catch (error) {
      // If account deletion succeeds but we get an error, still clear tokens
      if (error instanceof Error && error.message.includes('200')) {
        tokenStorage.clearAll();
      }
      throw error;
    }
  }

  /**
   * Login user (placeholder for future implementation)
   */
  async login(): Promise<void> {
    // TODO: Implement when login endpoint is available in generated types
    throw new Error('Login endpoint not yet implemented');
  }

  /**
   * Refresh access token
   * Uses a valid refresh token to obtain new access and refresh tokens
   */
  async refreshToken(
    refreshToken: string,
  ): Promise<RefreshTokenResponse['data']> {
    const response = await this.makeRequest<RefreshTokenResponse['data']>(
      '/auth/refresh',
      {
        method: 'POST',
        body: { refreshToken },
        requiresAuth: false,
      },
    );

    // Store new tokens after successful refresh
    if (response.access_token && response.refresh_token) {
      const tokenData: TokenData = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
      tokenStorage.setTokens(tokenData);
    }

    return response;
  }

  /**
   * Verify Facebook deletion status
   * Verifies the status of a Facebook data deletion request using confirmation code
   */
  async verifyDeletionStatus(
    confirmationCode: string,
    signal?: AbortSignal,
  ): Promise<VerifyDeletionStatusResponse['data']> {
    return this.makeRequest<VerifyDeletionStatusResponse['data']>(
      '/auth/facebook/verify-deletion-status',
      {
        method: 'POST',
        body: { confirmation_code: confirmationCode },
        requiresAuth: false,
        signal,
      },
    );
  }

  // ============================================================
  // UNIFIED AUTH ENDPOINTS - New simplified authentication flow
  // ============================================================

  /**
   * Unified email authentication
   * Handles both login and registration in one endpoint
   * Backend auto-detects if user exists and sends appropriate email
   * @param email - User's email address
   * @param locale - Language preference for email templates (default: 'en')
   */
  async unifiedEmailAuth(
    email: string,
    locale: string = 'en',
    consent: AuthUnifiedEmailConsentPayload | undefined = undefined,
  ): Promise<AuthUnifiedEmailAuthResponse> {
    const requestBody: AuthUnifiedEmailAuthRequestWithConsent = {
      email,
      locale: locale as UnifiedEmailAuthRequest['locale'],
      accepted_terms: consent?.accepted_terms,
      marketingEmails: consent?.marketingEmails,
    };

    return this.makeRequest<AuthUnifiedEmailAuthResponse>('/auth/email', {
      method: 'POST',
      body: requestBody,
      requiresAuth: false,
    });
  }

  /**
   * Verify unified email authentication token
   * Works for both login magic links and registration activation tokens
   * @param token - 6-digit verification token from email
   */
  async unifiedEmailVerify(
    token: string,
    signal?: AbortSignal,
  ): Promise<AuthPasswordlessVerifyResponse> {
    const response = await this.makeRequest<AuthPasswordlessVerifyResponse>(
      '/auth/passwordless/verify',
      {
        method: 'POST',
        body: { token },
        requiresAuth: false,
        signal,
      },
    );

    // Store tokens after successful verification
    if (response.access_token && response.refresh_token) {
      const tokenData: TokenData = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
      tokenStorage.setTokens(tokenData);
    }

    return response;
  }

  /**
   * Get unified OAuth provider URL
   * New endpoint that handles both login and registration automatically
   * @param provider - OAuth provider accepted by the unified OAuth API
   * @param locale - Language preference for OAuth flow
   */
  getUnifiedOAuthUrl(
    provider: UnifiedOAuthProviderValue,
    locale?: string,
  ): string {
    const apiInfo = this.getApiInfo();
    const baseUrl = `${apiInfo.apiBaseUrl}/auth/oauth/${provider}`;
    if (locale) {
      return `${baseUrl}?locale=${encodeURIComponent(locale)}`;
    }
    return baseUrl;
  }

  /**
   * Redirect to unified OAuth provider
   * Stores locale in localStorage before redirect for callback handling
   * @param provider - OAuth provider accepted by the unified OAuth API
   * @param locale - Language preference to store and use in OAuth flow
   */
  redirectToUnifiedOAuth(
    provider: UnifiedOAuthProviderValue,
    locale?: string,
  ): void {
    // Store locale in localStorage for OAuth callback
    if (locale) {
      try {
        localStorage.setItem('hopsa_locale', locale);
      } catch (error) {
        console.warn('Failed to store locale for OAuth:', error);
      }
    }
    window.location.href = this.getUnifiedOAuthUrl(provider, locale);
  }

  /**
   * Handle unified OAuth callback
   * Processes OAuth callback from social providers (unified flow)
   * @param provider - OAuth provider
   * @param code - Authorization code from OAuth provider
   * @param state - CSRF protection state token
   */
  async handleUnifiedOAuthCallback(
    provider: UnifiedOAuthProviderValue,
    code: string,
    state: string,
    signal?: AbortSignal,
  ): Promise<AuthOAuthCallbackResponse> {
    const params = new URLSearchParams();
    params.append('code', code);
    params.append('state', state);

    // NOTE: tokens are intentionally NOT persisted here. Persistence is
    // committed by the caller together with setting the user, so a failed/aborted
    // UI flow can never leave a stored session behind an "auth failed" screen.
    return this.makeRequest<AuthOAuthCallbackResponse>(
      `/auth/oauth/${provider}/callback?${params.toString()}`,
      {
        method: 'GET',
        requiresAuth: false,
        credentials: 'include',
        signal,
      },
    );
  }

  async finalizeOAuthRegistration(
    data: AuthOAuthFinalizeRequest,
    signal?: AbortSignal,
  ): Promise<AuthOAuthFinalizeResponse> {
    // See handleUnifiedOAuthCallback: token persistence is committed by the caller.
    return this.makeRequest<AuthOAuthFinalizeResponse>(
      '/auth/oauth/finalize',
      {
        method: 'POST',
        body: data,
        requiresAuth: false,
        credentials: 'include',
        signal,
      },
    );
  }

  // ============================================================
  // DEPRECATED OAUTH METHODS - Use unified methods above
  // ============================================================

  /**
   * Get OAuth provider URL for authentication
   * Uses the new unified OAuth endpoint structure
   * @deprecated Use getUnifiedOAuthUrl() instead
   */
  getOAuthUrl(
    provider: OAuthProviderValue,
    service: OAuthServiceValue,
  ): string {
    const apiInfo = this.getApiInfo();
    return `${apiInfo.apiBaseUrl}/auth/${provider}/${service}`;
  }

  /**
   * Redirect to OAuth provider for authentication
   * Uses window.location.href for full page redirect
   * @deprecated Use redirectToUnifiedOAuth() instead
   */
  redirectToOAuth(
    provider: OAuthProviderValue,
    service: OAuthServiceValue,
  ): void {
    window.location.href = this.getOAuthUrl(provider, service);
  }

  /**
   * Handle OAuth callback
   * Processes OAuth callback from social providers
   * @deprecated Use handleUnifiedOAuthCallback() instead
   */
  async handleOAuthCallback(
    provider: OAuthProviderValue,
    service: OAuthServiceValue,
    locale: string,
    code?: string,
    state?: string,
    signal?: AbortSignal,
  ): Promise<AuthOAuthCallbackResponse> {
    const params = new URLSearchParams();
    params.append('language', locale);
    if (code) params.append('code', code);
    if (state) params.append('state', state);

    const response = await this.makeRequest<AuthOAuthCallbackResponse>(
      `/auth/${provider}/${service}/callback?${params.toString()}`,
      {
        method: 'GET',
        requiresAuth: false,
        signal,
      },
    );

    // Store tokens after successful OAuth callback
    if (
      isAuthOAuthCallbackSuccessResponse(response) &&
      response.access_token &&
      response.refresh_token
    ) {
      const tokenData: TokenData = {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      };
      tokenStorage.setTokens(tokenData);
    }

    return response;
  }

  /**
   * Initiate Spotify OAuth login
   * Redirects to Spotify OAuth for authentication
   * @deprecated Use redirectToOAuth('spotify', 'login') instead
   */
  getSpotifyLoginUrl(): string {
    return this.getOAuthUrl('spotify', 'login');
  }

  /**
   * Initiate Spotify OAuth registration
   * Redirects to Spotify OAuth for account creation
   * @deprecated Use redirectToOAuth('spotify', 'register') instead
   */
  getSpotifyRegisterUrl(): string {
    return this.getOAuthUrl('spotify', 'register');
  }

  /**
   * Redirect to Spotify OAuth login
   * Uses window.location.href for full page redirect
   * @deprecated Use redirectToOAuth('spotify', 'login') instead
   */
  redirectToSpotifyLogin(): void {
    this.redirectToOAuth('spotify', 'login');
  }

  /**
   * Redirect to Spotify OAuth registration
   * Uses window.location.href for full page redirect
   * @deprecated Use redirectToOAuth('spotify', 'register') instead
   */
  redirectToSpotifyRegister(): void {
    this.redirectToOAuth('spotify', 'register');
  }
}

export const authApi = new AuthApiService();
